import React, { useState, useEffect, useCallback } from 'react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import { SpaceProvider, useSpace } from './contexts/SpaceContext';
import { InteractionProvider } from './contexts/InteractionContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Calendar from './components/calendar/Calendar';
import AuthForm from './components/auth/AuthForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import InviteAccept from './components/auth/InviteAccept';
import { OnboardingWizard } from './components/auth/OnboardingWizard';
import { LandingPage } from './components/landing/LandingPage';
import PricingPage from './components/landing/PricingPage';
import { PublicSurface } from './components/landing/PublicSurface';
import { PrivacyPolicy, TermsOfService } from './components/legal/LegalPage';
import { supabase } from './config/supabase';
import { installUserJot, userjot, USERJOT_PROJECT_ID } from './utils/userjot';

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = useCallback((path: string) => {
    window.history.pushState(null, '', path);
    setPathname(path);
  }, []);
  return { pathname, navigate };
}

function AppContent() {
  const { user, loading, passwordRecovery } = useAuth();
  const { theme } = useTheme();
  const { getMyPendingInvitations, respondToInvitation, refreshSpaces, setCurrentSpace } = useSpace();
  const { pathname, navigate } = usePathname();
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [pendingInvitations, setPendingInvitations] = useState<import('./contexts/SpaceContext').SpaceInvitation[]>([]);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});

  const [showOnboarding, setShowOnboarding] = useState(false);

  // UserJot — installed only once someone is signed in, so anonymous visitors
  // to the landing and legal pages load no third-party script at all. That is
  // what keeps the public surface free of non-essential cookies.
  useEffect(() => {
    if (!user?.email) return;
    installUserJot();

    const displayName = user.user_metadata?.display_name || user.email.split('@')[0];
    const [firstName, ...rest] = displayName.split(' ');

    const uj = userjot();
    uj?.init(USERJOT_PROJECT_ID, { widget: true, trigger: 'custom', theme });
    uj?.identify({
      id: user.id,
      email: user.email,
      firstName,
      lastName: rest.join(' ') || undefined,
    });
    // `theme` is intentionally not a dependency — the sync effect below owns it,
    // and re-running init on every theme change would re-register the widget.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Theme sync, only once the SDK has been installed
  useEffect(() => {
    if (!user) return;
    userjot()?.setTheme(theme);
  }, [theme, user?.id]);

  // Show onboarding wizard for new users; silently skip for existing users with a display name
  useEffect(() => {
    if (!user) return;
    const completed = !!user.user_metadata?.onboarding_completed;
    const hasDisplayName = !!user.user_metadata?.display_name;
    const resumingOnboarding = !!localStorage.getItem('mozayc-onboarding-resume-step');
    if (resumingOnboarding) {
      // Returning from OAuth mid-onboarding — always show wizard
      setShowOnboarding(true);
    } else if (completed) {
      setShowOnboarding(false);
    } else if (hasDisplayName) {
      // Existing user before onboarding was introduced — mark complete silently
      supabase.auth.updateUser({ data: { onboarding_completed: true } });
      setShowOnboarding(false);
    } else {
      setShowOnboarding(true);
    }
  }, [user?.id]);

  // Check for pending space invitations after login
  useEffect(() => {
    if (!user?.email) return;
    getMyPendingInvitations().then(setPendingInvitations);
  }, [user?.email]);

  const handleRespondToInvitation = async (token: string, accept: boolean) => {
    setRespondingTo(token);
    setInviteErrors(prev => { const next = { ...prev }; delete next[token]; return next; });
    const result = await respondToInvitation(token, accept);
    if (result.success) {
      if (accept && result.spaceId) {
        await refreshSpaces();
        const { data: spaceData } = await supabase
          .from('spaces')
          .select('*')
          .eq('id', result.spaceId)
          .single();
        if (spaceData) {
          setCurrentSpace({ ...spaceData, role: (result.role || 'viewer') as any, member_count: 1 });
        }
      }
      setPendingInvitations(prev => prev.filter(i => i.token !== token));
    } else {
      // Keep the notification visible and show the error
      setInviteErrors(prev => ({ ...prev, [token]: result.error || 'Failed to accept invitation' }));
    }
    setRespondingTo(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--surface-secondary)] flex items-center justify-center">
        <div className="text-[var(--content-primary)] text-xl">Loading...</div>
      </div>
    );
  }

  // Password recovery — show reset form before anything else
  if (passwordRecovery) {
    if (!user) {
      return (
        <div className="min-h-screen bg-[var(--surface-secondary)] flex items-center justify-center">
          <div className="text-[var(--content-primary)] text-xl">Loading...</div>
        </div>
      );
    }
    return <PublicSurface><ResetPasswordForm /></PublicSurface>;
  }

  // Legal pages — always accessible
  if (pathname === '/privacy') {
    return <PrivacyPolicy onBack={() => navigate('/')} />;
  }
  if (pathname === '/terms') {
    return <TermsOfService onBack={() => navigate('/')} />;
  }

  // Pricing — public, and a prerequisite for the Paddle domain approval
  if (pathname === '/pricing') {
    return (
      <PricingPage
        onGoToLogin={() => { setAuthMode('signin'); navigate('/app'); }}
        onGoToSignUp={() => { setAuthMode('signup'); navigate('/app'); }}
        isLoggedIn={!!user}
      />
    );
  }

  // Invite accept page — accessible before login (handles own auth via OTP)
  if (pathname === '/invite') {
    return <PublicSurface><InviteAccept /></PublicSurface>;
  }

  // Landing page — always accessible at /
  if (pathname === '/' || pathname === '') {
    return (
      <LandingPage
        onGoToLogin={() => { setAuthMode('signin'); navigate('/app'); }}
        onGoToSignUp={() => { setAuthMode('signup'); navigate('/app'); }}
        isLoggedIn={!!user}
      />
    );
  }

  // /app and /login — require auth
  if (!user) {
    return (
      <PublicSurface>
        <AuthForm onBack={() => navigate('/')} initialMode={authMode} />
      </PublicSurface>
    );
  }

  return (
    <>
      <Calendar />
      {showOnboarding && (
        <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
      )}
      {/* Pending space invitations */}
      {pendingInvitations.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
          {pendingInvitations.map((inv) => (
            <div
              key={inv.id}
              className="bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl shadow-lg p-4 animate-fadeIn"
            >
              <p className="text-sm font-medium text-[var(--content-primary)] mb-1">
                Space invitation
              </p>
              <p className="text-xs text-[var(--content-secondary)] mb-3">
                You've been invited to join <strong>{inv.space_name || 'a space'}</strong> as {inv.role}.
              </p>
              {inviteErrors[inv.token] && (
                <p className="text-xs text-[var(--error)] mb-2">{inviteErrors[inv.token]}</p>
              )}
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => handleRespondToInvitation(inv.token, false)}
                  disabled={respondingTo === inv.token}
                  className="px-3 py-1.5 text-xs font-medium text-[var(--content-secondary)] bg-[var(--surface-tertiary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors disabled:opacity-50"
                >
                  Decline
                </button>
                <button
                  onClick={() => handleRespondToInvitation(inv.token, true)}
                  disabled={respondingTo === inv.token}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--accent-primary)] rounded-lg hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-50"
                >
                  {respondingTo === inv.token ? 'Joining...' : 'Accept'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <PrivacyProvider>
        <InteractionProvider>
          <AuthProvider>
            <ToastProvider>
              <SpaceProvider>
                <NotificationProvider>
                  <AppContent />
                </NotificationProvider>
              </SpaceProvider>
            </ToastProvider>
          </AuthProvider>
        </InteractionProvider>
      </PrivacyProvider>
      <SpeedInsights />
      <Analytics />
    </ThemeProvider>
  );
}

export default App;